import { useEffect, useRef, useState } from "react";
import { achievementTileStyles } from "./AchievementTileStyles";

// MANAGEMENT ("Toplam"/total) COUNT achievements render the higher tier art (level 2..6, the bigger
// numbers) rather than the per-subject art (level 1..5). ACTIVITY ships a SEPARATE "-total" art set; these
// families instead share one art set numbered level-1..6, so their total tier is just level+1.
// LOGIN / DUEL only ship level-1..5 art, so they take no offset.
// Achievement art and its tier offsets are keyed by the wire value, so a renamed source type would
// miss both — a 404 for the art, and a silently wrong level for the offset. The backend may still
// send the old name (its @JsonCreator is inbound-only, and a client can outlive a backend deploy in
// either direction), so every lookup normalizes through here first. A missing base image is not one
// missing picture: its onLoad drives the overlay measurement, so the whole tile collapses.
const WIRE_TO_ASSET_SOURCE_TYPE: Record<string, string> = {
  CORPORATE_ASSIGNMENT: "BRANCH_ASSIGNMENT",
  // This family has been renamed twice on the wire; a client can meet either older name from a
  // backend it outlived in either direction, so both normalize to the current one.
  STUDY_GROUP: "SUPPORT_ASSIGNMENT",
  GROUP_ASSIGNMENT: "SUPPORT_ASSIGNMENT",
};

const assetSourceType = (sourceType: string) =>
  WIRE_TO_ASSET_SOURCE_TYPE[sourceType] || sourceType;

const MANAGEMENT_TOTAL_OFFSET_SOURCE_TYPES = [
  "ACTIVITY_CORRECT",
  "ACTIVITY_REVIEW",
  "ACTIVITY_TIME_SPENT",
  "CONTEST",
  "BRANCH_ASSIGNMENT",
  "PERSONALIZED_ASSIGNMENT",
];

// `earnedAchievementDTO` and its nested `achievementDTO` mirror the backend earned-achievement JSON
// object shape.
interface AchievementTileProps {
  earnedAchievementDTO: any;
  grayscale?: boolean;
  size?: string;
}

const AchievementTile = (props: AchievementTileProps) => {
  const { earnedAchievementDTO, grayscale, size } = props;
  const { achievementDTO, level } = earnedAchievementDTO;
  const { coterieType, sourceType, trackingType } = achievementDTO;
  const imageCategoryRef = useRef<HTMLImageElement | null>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!imageCategoryRef) return;
    if (!imageCategoryRef.current) return;
    if (!loaded) return;
    const { offsetWidth, offsetHeight } = imageCategoryRef.current;
    setWidth(offsetWidth);
    setHeight(offsetHeight);
  }, [imageCategoryRef, loaded]);

  const constructSourceTypeUrl = (sourceType: string) => {
    return `/achievements/source-type-${assetSourceType(sourceType)
      .toLowerCase()
      .replaceAll("_", "-")}.webp`;
  };

  const constructCoterieTypeUrl = (coterieType: string) => {
    let currentCoterieType = coterieType;
    if (currentCoterieType === "MANAGEMENT") {
      currentCoterieType = "TOTAL";
    }
    currentCoterieType = currentCoterieType.toLowerCase().replaceAll("_", "-");
    return `/achievements/coterie-type-${currentCoterieType}.webp`;
  };

  const constructTrackingTypeUrl = (
    sourceType: string,
    trackingType: string,
    coterieType: string,
    level: number,
  ) => {
    const currentTrackingType = trackingType.toLowerCase().replaceAll("_", "-");
    let currentSourceType = assetSourceType(sourceType)
      .toLowerCase()
      .replaceAll("_", "-");
    let currentLevel = level;
    if (coterieType === "MANAGEMENT" && trackingType === "COUNT") {
      if (sourceType === "ACTIVITY") {
        // ACTIVITY ships a separate "-total" art set numbered level-2..6 (no level-1).
        currentSourceType += "-total";
        currentLevel = currentLevel + 1;
      } else if (MANAGEMENT_TOTAL_OFFSET_SOURCE_TYPES.includes(assetSourceType(sourceType))) {
        // Shared art set numbered level-1..6; the "total" tier uses the higher level-2..6 images.
        currentLevel = currentLevel + 1;
      }
      // STREAK / POINT_TOTAL (non-COUNT) and LOGIN / DUEL (only level-1..5 art) take no offset.
    }

    return `/achievements/tracking-type-${currentTrackingType}-${currentSourceType}-level-${currentLevel}.webp`;
  };

  const constructDegreeUrl = (level: number) => {
    return `/achievements/degree-level-${level}.webp`;
  };

  let className = "";

  if (size === "xsmall") {
    className = "qad-size-xsmall";
  } else if (size === "small") {
    className = "qad-size-small";
  } else if (size === "medium") {
    className = "qad-size-medium";
  } else if (size === "large") {
    className = "qad-size-large";
  } else if (size === "xlarge") {
    className = "qad-size-xlarge";
  } else if (size === "2xlarge") {
    className = "qad-size-2xlarge";
  } else if (size === "3xlarge") {
    className = "qad-size-3xlarge";
  } else if (size === "4xlarge") {
    className = "qad-size-4xlarge";
  } else if (size === "5xlarge") {
    className = "qad-size-5xlarge";
  } else if (size === "6xlarge") {
    className = "qad-size-6xlarge";
  } else if (size === "7xlarge") {
    className = "qad-size-7xlarge";
  } else if (size === "8xlarge") {
    className = "qad-size-8xlarge";
  }

  const sourceTypeUrl = constructSourceTypeUrl(sourceType);
  const coterieTypeUrl = constructCoterieTypeUrl(coterieType);
  const trackingTypeUrl = constructTrackingTypeUrl(
    sourceType,
    trackingType,
    coterieType,
    level,
  );
  const degreeUrl = constructDegreeUrl(level);

  const handleOnLoad = () => {
    setLoaded(true);
  };

  return (
    <div
      className={`qad-root ${grayscale ? "qad-grayscale" : ""}`}
    >
      <style>{achievementTileStyles}</style>
      <img
        ref={imageCategoryRef}
        src={sourceTypeUrl}
        onLoad={handleOnLoad}
        className={`${className}`}
        alt="achievement-source-type"
      />
      <div
        className="qad-overlay-mid"
        style={{ width: width * 0.6, left: width * 0.35, top: height * 0.4 }}
      >
        <img
          src={coterieTypeUrl}
          className="qad-image-fill"
          alt="achievement-coterie-type"
        />
      </div>
      <div
        className="qad-overlay-mid"
        style={{ width: width * 0.5, left: width * 0.05, top: height * 0.25 }}
      >
        <img src={degreeUrl} className="qad-image-fill" alt="achievement-degree" />
      </div>
      <div
        className="qad-overlay-top"
        style={{
          height: height * 0.3,
          top: height * 0.05,
          right: 0,
        }}
      >
        <img
          src={trackingTypeUrl}
          className="qad-image-fill"
          alt="achievement-level"
        />
      </div>
    </div>
  );
};

export default AchievementTile;
